import { useEffect } from 'react'
import { Screen, SectionHeader, ViewAllLink } from '@/components/layout'
import { Button, Banner } from '@/components/primitives'
import { MetricRow, RecommendationCard, type MetricSpec } from '@/components/cards'
import { ScoreDial } from '@/components/charts/ScoreDial'
import { EmptyState, ErrorState, LoadingRegion, SkeletonCard, SkeletonList, SkeletonMetrics } from '@/components/states'
import { GeneratedText } from '@/components/GeneratedText'
import { List, TransactionRow } from '@/components/rows'
import { api } from '@/lib/api'
import { useProseState } from '@/lib/analysis'
import { t, type CopyKey } from '@/lib/copy'
import { formatMonth, formatMoney, formatPercent } from '@/lib/format'
import { Link, useNavigate } from '@/lib/router'
import { combine, useAsync } from '@/lib/useAsync'
import { clearImportPending, pendingImportSince } from '@/lib/importSignal'
import type { ScoreBand } from '@/lib/tokens'

/**
 * Home. Distinguishes `analyzing` (import done, analysis pending — the last
 * completed analysis stays on screen) from `loading` and from `partial`
 * (an account has a gap in statement coverage).
 *
 * `analysisPending` and `analysisFailed` are a SEPARATE axis from those. They
 * describe only whether model-written prose is coming, never whether the screen
 * has data, so they sit on top of `ready` and change nothing else: no spinner,
 * no skeleton, no banner, no disabled control. The one slot they touch is the
 * summary under the dial, which shows its copyKey until prose arrives.
 */
export function DashboardScreen() {
  const navigate = useNavigate()

  const score = useAsync(() => api.analysis.score(), [])
  const cashFlow = useAsync(() => api.analysis.cashFlow(12), [])
  const recommendations = useAsync(() => api.recommendations.list({ status: 'open' }), [])
  const accounts = useAsync(() => api.accounts.list(), [])
  const recent = useAsync((signal) => api.transactions.list({ limit: 5 }, signal), [])

  const loaded = combine(score.state, cashFlow.state, recommendations.state, accounts.state)
  const pendingSince = pendingImportSince()

  const scoreData = score.state.data
  const analysisIsStale = Boolean(pendingSince && scoreData && scoreData.computedAt < pendingSince)

  useEffect(() => {
    if (pendingSince && scoreData && scoreData.computedAt >= pendingSince) clearImportPending()
  }, [pendingSince, scoreData])

  // Re-poll while the Server is still recomputing.
  useEffect(() => {
    if (!analysisIsStale) return
    const timer = setTimeout(() => score.reload(), 2000)
    return () => clearTimeout(timer)
  }, [analysisIsStale, score])

  const hasAccounts = (accounts.state.data?.length ?? 0) > 0
  const gaps = (accounts.state.data ?? []).flatMap((a) => a.coverage?.gaps ?? [])

  // Prose only. Polls while the score says a narrative is being written, and
  // reloads the score when it lands — see analysis-status.schema.json x-polling.
  const prose = useProseState(scoreData?.narrativeState, score.reload)

  const dataState =
    loaded.status === 'error' && !loaded.data
      ? 'error'
      : loaded.status === 'loading' && !loaded.data
        ? 'loading'
        : accounts.state.data && !hasAccounts
          ? 'empty'
          : analysisIsStale
            ? 'analyzing'
            : gaps.length > 0
              ? 'partial'
              : 'ready'

  // `analyzing` and `partial` outrank the prose states: they say something
  // about the data itself, which matters more than whether a sentence is
  // outstanding. Everything below only ever refines `ready`.
  const screenState = dataState === 'ready' ? (prose.state ?? 'ready') : dataState

  // Everything past the three early returns below renders one tree, and both
  // prose states are in it. `analysisPending` adds no spinner and disables no
  // control — SPEC.md is explicit that neither may block this screen — and
  // `analysisFailed` is not the error treatment. All either one changes is
  // whether a slot holds prose or its copyKey.
  const rendersContent =
    screenState === 'ready' ||
    screenState === 'analyzing' ||
    screenState === 'partial' ||
    screenState === 'analysisPending' ||
    screenState === 'analysisFailed'

  const month = cashFlow.state.data?.months.at(-1)

  const metrics: MetricSpec[] = month
    ? [
        {
          key: 'net',
          label: t('dashboard.metric.netCashFlow'),
          value: formatMoney(month.net, { signed: true }),
          tone: month.net.amount >= 0 ? 'positive' : 'negative',
        },
        { key: 'income', label: t('dashboard.metric.income'), value: formatMoney(month.income, { absolute: true }) },
        { key: 'spending', label: t('dashboard.metric.spending'), value: formatMoney(month.spending, { absolute: true }) },
        {
          key: 'savingsRate',
          label: t('dashboard.metric.savingsRate'),
          value: formatPercent(cashFlow.state.data?.savingsRate, 1),
        },
      ]
    : []

  const deltaLabel = (() => {
    if (!scoreData || scoreData.delta === null || scoreData.delta === undefined) return undefined
    const period = t(`dashboard.score.period.${scoreData.deltaPeriod ?? 'lastMonth'}` as CopyKey)
    if (scoreData.delta > 0) return t('dashboard.score.delta.up', { points: scoreData.delta, period })
    if (scoreData.delta < 0) return t('dashboard.score.delta.down', { points: -scoreData.delta, period })
    return t('dashboard.score.delta.flat', { period })
  })()

  if (screenState === 'loading') {
    return (
      <Screen id="dashboard" state={screenState}>
        <LoadingRegion>
          <SkeletonCard height="var(--space-xxl)" />
          <SkeletonMetrics />
          <SkeletonList rows={3} />
        </LoadingRegion>
      </Screen>
    )
  }

  if (screenState === 'error') {
    return (
      <Screen id="dashboard" state={screenState}>
        <ErrorState
          titleKey="dashboard.error.title"
          error={loaded.status === 'error' ? loaded.error : undefined}
          onRetry={() => {
            score.reload()
            cashFlow.reload()
            recommendations.reload()
            accounts.reload()
          }}
        />
      </Screen>
    )
  }

  if (screenState === 'empty') {
    return (
      <Screen id="dashboard" state={screenState}>
        <EmptyState
          titleKey="dashboard.empty.title"
          actionLabel={t('dashboard.empty.cta')}
          onAction={() => navigate('upload')}
          secondaryLabel={t('dashboard.empty.link')}
          onSecondary={() => navigate('linkAccount')}
        />
      </Screen>
    )
  }

  // Exhaustiveness, not a branch: the three returns above take every state
  // that replaces the screen, so the five in `rendersContent` are what is
  // left, and they all render the tree below unchanged.
  if (!rendersContent) return null

  const openRecommendations = recommendations.state.data ?? []

  return (
    <Screen
      id="dashboard"
      state={screenState}
      subtitle={month ? t('dashboard.greeting', { month: formatMonth(month.month) }) : undefined}
      actions={
        <Button variant="primary" onClick={() => navigate('upload')}>
          {t('dashboard.cta.upload')}
        </Button>
      }
    >
      {screenState === 'analyzing' ? <Banner tone="info">{t('dashboard.analyzing.title')}</Banner> : null}
      {screenState === 'partial' ? (
        <Banner
          tone="caution"
          action={
            <Button variant="quiet" onClick={() => navigate('upload')}>
              {t('dashboard.partial.cta')}
            </Button>
          }
        >
          {t('dashboard.partial.banner', { count: gaps.length })}
        </Banner>
      ) : null}

      <nav className="screen-links" aria-label={t('dashboard.title')}>
        <Link screen="accounts" className="btn btn-secondary">{t('accounts.title')}</Link>
        <Link screen="cashFlow" className="btn btn-secondary">{t('cashFlow.title')}</Link>
        <Link screen="debt" className="btn btn-secondary">{t('debt.title')}</Link>
        <Link screen="fees" className="btn btn-secondary">{t('fees.title')}</Link>
        <Link screen="recurring" className="btn btn-secondary">{t('recurring.title')}</Link>
        <Link screen="savings" className="btn btn-secondary">{t('savings.title')}</Link>
      </nav>

      <div className="card card-pad-lg">
        <div className="row" style={{ gap: 'var(--space-xl)', flexWrap: 'wrap' }}>
          {scoreData ? (
            <button
              type="button"
              className="btn btn-quiet"
              style={{ padding: 0, height: 'auto' }}
              onClick={() => navigate('healthScore')}
              aria-label={t('dashboard.score.label')}
            >
              <ScoreDial score={scoreData.score} band={scoreData.band as ScoreBand} size={180} />
            </button>
          ) : null}
          <div className="stack-sm" style={{ flex: 1, minWidth: 0 }}>
            <span className="metric-label">{t('dashboard.score.label')}</span>
            {deltaLabel ? <span className="body-secondary">{deltaLabel}</span> : null}
            {/* One slot, two sources. In analysisPending this is the keyed
                sentence and in ready it may be the model's — same element,
                same place, so nothing moves when prose arrives. */}
            <GeneratedText
              text={scoreData?.narrative?.summary}
              copyKey="dashboard.score.summary"
              className="body-secondary"
            />
            <MetricRow metrics={metrics} />
          </div>
        </div>
      </div>

      <section>
        <SectionHeader
          title={t('dashboard.section.recommendations')}
          action={
            openRecommendations.length > 0 ? (
              <ViewAllLink screen="recommendations" label={t('dashboard.cta.allRecommendations')} />
            ) : undefined
          }
        />
        {recommendations.state.status === 'loading' && !recommendations.state.data ? (
          <SkeletonList rows={3} />
        ) : openRecommendations.length === 0 ? (
          <EmptyState titleKey="recommendations.empty.title" />
        ) : (
          <div className="stack-sm">
            {openRecommendations.slice(0, 3).map((rec) => (
              <RecommendationCard
                key={rec.id}
                recommendation={rec}
                onSelect={() => navigate('recommendationDetail', { id: rec.id })}
              />
            ))}
          </div>
        )}
      </section>

      <section>
        <SectionHeader title={t('dashboard.section.recentActivity')} action={<ViewAllLink screen="transactions" />} />
        {recent.state.data ? (
          <List>
            {recent.state.data.items.map((tx) => (
              <TransactionRow
                key={tx.id}
                transaction={tx}
                onSelect={() => navigate('transactionDetail', { id: tx.id })}
              />
            ))}
          </List>
        ) : (
          <SkeletonList rows={4} />
        )}
      </section>
    </Screen>
  )
}
